/* Hallmark · component: question-bank-editor · genre: editorial · theme: Operational Architect
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass via semantic Operational Architect tokens
 */
import { useMemo, useState } from 'react';
import { CheckCircle2, Plus, Save, Trash2, X } from 'lucide-react';
import { Button } from '@lms/ui';
import type { Program, QuestionBankQuestion, QuestionType } from '@lms/shared/types';
import { useProgram } from '../hooks/usePrograms';
import {
  type QuestionBankOptionInput,
  type QuestionBankQuestionInput,
  useCreateQuestionBankQuestion,
  useUpdateQuestionBankQuestion,
} from '../hooks/useQuestionBank';
import { getProgramSubjects, multipleChoiceOptions, trueFalseOptions } from './question-bank-utils';

interface QuestionBankEditorDialogProps {
  programs: Program[];
  question?: QuestionBankQuestion;
  onClose: () => void;
  onSaved: () => void;
}

function makeInitial(question?: QuestionBankQuestion): QuestionBankQuestionInput {
  if (question) {
    return {
      programId: question.programId,
      subjectId: question.subjectId ?? undefined,
      question: question.question,
      type: question.type,
      explanation: question.explanation ?? undefined,
      points: question.points,
      options: question.options.map((option) => ({
        text: option.text,
        isCorrect: option.isCorrect,
        order: option.order,
      })),
    };
  }
  return {
    programId: '',
    subjectId: undefined,
    question: '',
    type: 'MULTIPLE_CHOICE',
    explanation: '',
    points: 10,
    options: multipleChoiceOptions,
  };
}

export function QuestionBankEditorDialog({
  programs,
  question,
  onClose,
  onSaved,
}: QuestionBankEditorDialogProps) {
  const [form, setForm] = useState<QuestionBankQuestionInput>(() => makeInitial(question));
  const [error, setError] = useState('');
  const { data: selectedProgram } = useProgram(form.programId);
  const subjects = useMemo(() => getProgramSubjects(selectedProgram), [selectedProgram]);
  const createQuestion = useCreateQuestionBankQuestion();
  const updateQuestion = useUpdateQuestionBankQuestion();
  const isPending = createQuestion.isPending || updateQuestion.isPending;
  const isObjective = form.type === 'MULTIPLE_CHOICE' || form.type === 'TRUE_FALSE';

  const setQuestionType = (type: QuestionType) => {
    setForm((current) => ({
      ...current,
      type,
      options:
        type === 'TRUE_FALSE'
          ? trueFalseOptions
          : type === 'ESSAY' || type === 'SHORT_ANSWER'
            ? []
            : current.options.length >= 2
              ? current.options
              : multipleChoiceOptions,
    }));
  };

  const updateOption = (index: number, patch: Partial<QuestionBankOptionInput>) => {
    setForm((current) => ({
      ...current,
      options: current.options.map((option, optionIndex) =>
        optionIndex === index ? { ...option, ...patch } : option,
      ),
    }));
  };

  const addOption = () => {
    setForm((current) => ({
      ...current,
      options: [
        ...current.options,
        { text: '', isCorrect: false, order: current.options.length + 1 },
      ],
    }));
  };

  const removeOption = (index: number) => {
    setForm((current) => ({
      ...current,
      options: current.options
        .filter((_, optionIndex) => optionIndex !== index)
        .map((option, optionIndex) => ({ ...option, order: optionIndex + 1 })),
    }));
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (!form.programId) {
      setError('Pilih program untuk mengategorikan soal.');
      return;
    }
    try {
      if (question) {
        await updateQuestion.mutateAsync({ id: question.id, data: form });
      } else {
        await createQuestion.mutateAsync(form);
      }
      onSaved();
      onClose();
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : 'Soal belum dapat disimpan.');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-on-surface/45 p-0 backdrop-blur-sm sm:items-center sm:p-6">
      <form
        onSubmit={submit}
        className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-t-[1.75rem] bg-surface-container-lowest shadow-level5 sm:rounded-[1.75rem]"
        aria-labelledby="question-bank-editor-title"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 bg-surface-container-lowest/90 p-6 backdrop-blur-xl sm:p-8">
          <div>
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
              Bank soal bersama
            </p>
            <h2 id="question-bank-editor-title" className="mt-1 font-headline text-2xl font-bold tracking-tight text-on-surface">
              {question ? 'Perbarui soal' : 'Tulis soal baru'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            aria-label="Tutup editor soal"
            className="grid size-11 place-items-center rounded-xl text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-40 active:scale-95"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-7 p-6 pt-2 sm:p-8 sm:pt-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="bank-program" className="mb-2 block text-xs font-label font-bold text-on-surface">
                Program <span className="text-error">*</span>
              </label>
              <select
                id="bank-program"
                value={form.programId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    programId: event.target.value,
                    subjectId: undefined,
                  }))
                }
                className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-sm text-on-surface outline-none transition focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Pilih program</option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>{program.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="bank-subject" className="mb-2 block text-xs font-label font-bold text-on-surface">
                Mata pelajaran <span className="font-normal text-on-surface-variant">(opsional)</span>
              </label>
              <select
                id="bank-subject"
                value={form.subjectId ?? ''}
                onChange={(event) => setForm((current) => ({ ...current, subjectId: event.target.value || undefined }))}
                disabled={!form.programId}
                className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-sm text-on-surface outline-none transition focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Umum untuk program</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="bank-question" className="mb-2 block text-xs font-label font-bold text-on-surface">
              Teks soal <span className="text-error">*</span>
            </label>
            <textarea
              id="bank-question"
              value={form.question}
              onChange={(event) => setForm((current) => ({ ...current, question: event.target.value }))}
              required
              rows={4}
              className="w-full resize-y rounded-2xl bg-surface-container-high px-4 py-3 text-sm leading-relaxed text-on-surface outline-none transition focus:ring-2 focus:ring-primary/20"
              placeholder="Tuliskan pertanyaan secara lengkap…"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_9rem]">
            <div>
              <label htmlFor="bank-type" className="mb-2 block text-xs font-label font-bold text-on-surface">Tipe soal</label>
              <select
                id="bank-type"
                value={form.type}
                onChange={(event) => setQuestionType(event.target.value as QuestionType)}
                className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-sm text-on-surface outline-none transition focus:ring-2 focus:ring-primary/20"
              >
                <option value="MULTIPLE_CHOICE">Pilihan Ganda</option>
                <option value="TRUE_FALSE">Benar / Salah</option>
                <option value="ESSAY">Esai</option>
              </select>
            </div>
            <div>
              <label htmlFor="bank-points" className="mb-2 block text-xs font-label font-bold text-on-surface">Bobot</label>
              <input
                id="bank-points"
                type="number"
                min={1}
                value={form.points}
                onChange={(event) => setForm((current) => ({ ...current, points: Number(event.target.value) || 1 }))}
                className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-sm text-on-surface outline-none transition focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {isObjective && (
            <section className="rounded-2xl bg-surface-container-low p-4 sm:p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h3 className="m-0 text-sm font-headline font-bold text-on-surface">Pilihan jawaban</h3>
                  <p className="mt-1 text-xs text-on-surface-variant">Tandai satu jawaban yang benar.</p>
                </div>
                {form.type === 'MULTIPLE_CHOICE' && (
                  <Button type="button" variant="outline" onClick={addOption} className="h-9 rounded-lg px-3 text-xs font-bold active:scale-95">
                    <Plus size={15} /> Tambah opsi
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                {form.options.map((option, index) => (
                  <div key={`${option.order}-${index}`} className="flex items-center gap-3 rounded-xl bg-surface-container-lowest p-3">
                    <input
                      type="radio"
                      name="correct-answer"
                      checked={option.isCorrect}
                      onChange={() => setForm((current) => ({
                        ...current,
                        options: current.options.map((currentOption, optionIndex) => ({
                          ...currentOption,
                          isCorrect: optionIndex === index,
                        })),
                      }))}
                      aria-label={`Tandai opsi ${index + 1} sebagai jawaban benar`}
                      className="size-4 accent-primary"
                    />
                    <input
                      value={option.text}
                      onChange={(event) => updateOption(index, { text: event.target.value })}
                      disabled={form.type === 'TRUE_FALSE'}
                      aria-label={`Teks opsi ${index + 1}`}
                      className="min-w-0 flex-1 bg-transparent text-sm text-on-surface outline-none placeholder:text-on-surface-variant/60 disabled:cursor-not-allowed"
                      placeholder={`Opsi ${index + 1}`}
                    />
                    {option.isCorrect && <CheckCircle2 size={16} className="shrink-0 text-primary" aria-hidden="true" />}
                    {form.type === 'MULTIPLE_CHOICE' && form.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        aria-label={`Hapus opsi ${index + 1}`}
                        className="grid size-8 place-items-center rounded-lg text-on-surface-variant hover:bg-error-container hover:text-on-error-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
                      >
                        <Trash2 size={15} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          <div>
            <label htmlFor="bank-explanation" className="mb-2 block text-xs font-label font-bold text-on-surface">
              Pembahasan <span className="font-normal text-on-surface-variant">(opsional)</span>
            </label>
            <textarea
              id="bank-explanation"
              value={form.explanation ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, explanation: event.target.value }))}
              rows={3}
              className="w-full resize-y rounded-2xl bg-surface-container-high px-4 py-3 text-sm leading-relaxed text-on-surface outline-none transition focus:ring-2 focus:ring-primary/20"
              placeholder="Tambahkan alasan atau rujukan jawaban…"
            />
          </div>

          {error && <p role="alert" className="rounded-xl bg-error-container px-4 py-3 text-xs font-medium text-on-error-container">{error}</p>}
          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isPending} className="h-11 rounded-xl px-5 text-xs font-bold active:scale-95">Batal</Button>
            <Button type="submit" variant="primary" disabled={isPending} className="h-11 rounded-xl px-5 text-xs font-bold active:scale-95">
              <Save size={16} /> {isPending ? 'Menyimpan…' : 'Simpan soal'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
